/**
 * Partial GraphQL updates (see FRONTEND_OVERVIEW.md §3 Mutations).
 *
 * Update inputs use strawberry.UNSET: omitted fields stay unchanged,
 * explicit `null` clears a nullable field. Never send a bag of nulls
 * for untouched fields.
 *
 * Build update variables with `undefined` for untouched keys, then pass
 * through `omitUndefined` (or use `createGraphqlClient`, which does this).
 */

export type PlainObject = Record<string, unknown>;

/** Deep-omit `undefined` only — preserves `null` (clear) and empty arrays/objects. */
export function omitUndefined<T>(value: T): T {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => omitUndefined(item)) as T;
  }

  const result: PlainObject = {};
  for (const [key, entry] of Object.entries(value as PlainObject)) {
    if (entry === undefined) continue;
    result[key] = omitUndefined(entry);
  }
  return result as T;
}

/**
 * Build a partial update payload: always include `id`, drop other undefined keys.
 * Explicit `null` values are kept so the API can clear nullable fields.
 */
export function partialUpdateInput<T extends { id: string }>(
  id: string,
  patch: Omit<{ [K in keyof T]?: T[K] | null | undefined }, "id">,
): PlainObject {
  return omitUndefined({ id, ...patch });
}
